import React, { useState } from "react";
import { fetchApi } from "../../config";

const EMPTY = { name: "", email: "", company: "", phone: "", message: "" };

export default function ContactForm() {
  const [values, setValues] = useState(EMPTY);
  const [status, setStatus] = useState(null); // null | "sending" | "sent" | "error"
  const [errors, setErrors] = useState({});

  const validateField = (name, value) => {
    const trimmed = (value || "").trim();
    const emailRe = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const phoneRe = /^[+()\-\s\d]{7,}$/;

    if (name === "name") {
      if (!trimmed) return "Please enter your full name.";
      if (trimmed.length < 2) return "That name looks too short.";
    } else if (name === "email") {
      if (!trimmed) return "Please enter your email address.";
      if (!emailRe.test(trimmed)) return "Please enter a valid email address.";
    } else if (name === "company") {
      if (!trimmed) return "Please enter your company name.";
      if (trimmed.length < 2) return "That company name looks too short.";
    } else if (name === "phone") {
      if (!trimmed) return "Please enter a phone number.";
      if (!phoneRe.test(trimmed)) return "Please enter a valid phone number.";
    } else if (name === "message") {
      if (!trimmed) return "Please tell us a little about your project.";
      if (trimmed.length < 10)
        return "A few more details would help (10+ characters).";
    }
    return "";
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setValues((v) => ({ ...v, [name]: value }));
    if (errors[name]) {
      const nextError = validateField(name, value);
      setErrors((prev) => ({ ...prev, [name]: nextError }));
    }
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    setErrors((prev) => ({ ...prev, [name]: validateField(name, value) }));
  };

  const validateAll = () => {
    const nextErrors = {};
    Object.entries(values).forEach(([name, value]) => {
      const message = validateField(name, value);
      if (message) nextErrors[name] = message;
    });
    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateAll()) {
      return;
    }
    setStatus("sending");
    try {
      const payload = new URLSearchParams();
      payload.append("name", values.name);
      payload.append("email", values.email);
      payload.append("company", values.company);
      payload.append("phone", values.phone);
      payload.append("message", values.message);

      const response = await fetchApi("contact_form_submit.php", {
        method: "POST",
        body: payload,
      });

      // Expecting JSON response with a `result` boolean
      if (response && (response.result === true || response.success === true)) {
        setStatus("sent");
        setValues(EMPTY);
        setErrors({});
      } else {
        const msg = (response && (response.message || response.error)) ||
          "Failed to submit form.";
        setStatus("error");
        setErrors((prev) => ({ ...prev, _form: msg }));
      }
    } catch (err) {
      setStatus("error");
      setErrors((prev) => ({ ...prev, _form: err.message || String(err) }));
    }
  };

  const handleDone = () => {
    setStatus(null);
    setErrors({});
  };

  if (status === "sent") {
    return (
      <div className="modal-step">
        <div className="modal-done">
          <div className="modal-success-check">✓</div>
          <h3>Thank you!</h3>
          <p className="sub">
            We'll review your query and get back within one business day.
          </p>
          <button className="btn btn-primary btn--sm" onClick={handleDone} type="button">
            Done
          </button>
        </div>
      </div>
    );
  }

  return (
    <form className="contact-form" noValidate onSubmit={handleSubmit}>
      <div className="form-grid-2">
        <div className="field">
          <label htmlFor="cf-name">Full name</label>
          <input
            className="input"
            id="cf-name"
            name="name"
            onBlur={handleBlur}
            onChange={handleChange}
            placeholder="Jane Doe"
            required
            value={values.name}
          />
          {errors.name && <div className="error-msg">{errors.name}</div>}
        </div>
        <div className="field">
          <label htmlFor="cf-email">Email</label>
          <input
            className="input"
            id="cf-email"
            name="email"
            onBlur={handleBlur}
            onChange={handleChange}
            placeholder="jane@company.com"
            required
            type="email"
            value={values.email}
          />
          {errors.email && <div className="error-msg">{errors.email}</div>}
        </div>
        <div className="field">
          <label htmlFor="cf-company">Company</label>
          <input
            className="input"
            id="cf-company"
            name="company"
            onBlur={handleBlur}
            onChange={handleChange}
            placeholder="Company"
            value={values.company}
          />
          {errors.company && <div className="error-msg">{errors.company}</div>}
        </div>
        <div className="field">
          <label htmlFor="cf-phone">Phone</label>
          <input
            className="input"
            id="cf-phone"
            name="phone"
            onBlur={handleBlur}
            onChange={handleChange}
            placeholder="(123) 456 7890"
            type="tel"
            value={values.phone}
          />
          {errors.phone && <div className="error-msg">{errors.phone}</div>}
        </div>

        <div className="field">
          <label htmlFor="cf-message">Tell us about your project</label>
          <textarea
            className="textarea"
            id="cf-message"
            name="message"
            onBlur={handleBlur}
            onChange={handleChange}
            placeholder="A few sentences about what you need..."
            required
            rows="4"
            value={values.message}
          />
          {errors.message && <div className="error-msg">{errors.message}</div>}
        </div>
      </div>

      <button
        className="btn btn-primary btn--block"
        disabled={status === "sending"}
        type="submit"
      >
        {status === "sending" ? "Sending…" : "Send message"}
      </button>

      {status === "error" && (
        <p className="form-note">
          Something went wrong. Please email info@kudosta.com.
        </p>
      )}
    </form>
  );
}
